/* Supply › Reports › Purchase › Purchase Register
 * Posted/inward purchases (GRNs) over a date range, by supplier / warehouse.
 * Data: GET /v1/purchases (mapped to GRN rows). */
import React, { useEffect, useMemo, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum, cbStatusVariant } from "../supplyData";
import { fmtDate } from "../../../Utils/dateFormat";
import {
  Button,
  Pill,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack } from "./reportShared";
import { PHARMACY_ROUTES } from "../../../Route/path";

const PurchaseRegister = () => {
  /* Scoped to the store chosen in the topbar — one selection for the whole app,
     so the report has no warehouse filter of its own. */
  const { code: wh } = useWarehouse();
  const [suppliers, setSuppliers] = useState([]);
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState(() => ({
    supplierId: "",
    fromDate: "",
    toDate: "",
    sortDir: "desc",
  }));
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    let alive = true;
    /* Suppliers belong to a warehouse, so only this store's are offered. */
    supplyService
      .getSuppliers({ warehouseId: wh || undefined })
      .then((s) => {
        if (!alive) return;
        setSuppliers(s || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [reloadKey, wh]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = { sort: `invoiceDate,${filters.sortDir}`, size: 500 };
    if (filters.supplierId) params.supplierId = Number(filters.supplierId);
    if (wh) params.warehouseId = wh;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    supplyService
      .getGrns(params)
      .then((res) => {
        if (!alive) return;
        setGrns(res.content || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load purchase register.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh, filters.supplierId, filters.fromDate, filters.toDate, filters.sortDir]);

  useEffect(() => {
    setPage(0);
  }, [wh, filters.supplierId, filters.fromDate, filters.toDate, filters.sortDir, pageSize]);

  const supById = (id) => suppliers.find((s) => s.id === id);

  const totals = useMemo(
    () =>
      grns.reduce(
        (acc, g) => {
          acc.units += Number(g.units) || 0;
          acc.value += Number(g.value) || 0;
          acc.items += Number(g.items) || 0;
          return acc;
        },
        { units: 0, value: 0, items: 0 }
      ),
    [grns]
  );

  const totalRows = grns.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = grns.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const startRow = totalRows === 0 ? 0 : safePage * pageSize + 1;
  const endRow = Math.min(totalRows, safePage * pageSize + pageSize);

  const handleExport = () => {
    const headers = ["GRN #", "Date", "Supplier", "Sup. invoice", "Warehouse", "Items", "Units", "Value", "Status"];
    const rows = grns.map((g) => [
      g.id,
      g.date || "",
      supById(g.supplier)?.name || g.supplierName || "",
      g.supplierInv || "",
      g.wh || "",
      g.items,
      g.units,
      Number((Number(g.value) || 0).toFixed(2)),
      g.status,
    ]);
    exportToXlsx("purchase-register.xlsx", headers, rows, "Purchases");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.PURCHASE_REPORT} label="Purchase Reports" />
      <SectionHeader
        title="Purchase Register"
        sub="Inward purchases (GRNs) by supplier, warehouse, and date"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!grns.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <Select value={filters.supplierId} onChange={(e) => setF("supplierId", e.target.value)}>
          <option value="">All suppliers</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <Select value={filters.sortDir} onChange={(e) => setF("sortDir", e.target.value)} title="Sort by date">
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </Select>
        <Button
          onClick={() =>
            setFilters({
              supplierId: "",
              fromDate: "",
              toDate: "",
              sortDir: "desc",
            })
          }
        >
          Reset
        </Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(totalRows)} GRNs
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="GRNs" value={cbFmtNum(totalRows)} variant="ink" />
        <KpiBox label="Line items" value={cbFmtNum(totals.items)} variant="ink" />
        <KpiBox label="Units purchased" value={cbFmtNum(totals.units)} variant="ink" />
        <KpiBox label="Purchase value" value={cbFmtINR(totals.value)} variant="green" />
      </div>

      {loading ? (
        <LoadingState label="Loading purchase register…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            columns={[
              { label: "GRN #" },
              { label: "Date" },
              { label: "Supplier" },
              { label: "Sup. invoice" },
              { label: "Wh" },
              { label: "Items", align: "right" },
              { label: "Units", align: "right" },
              { label: "Value", align: "right" },
              { label: "Status" },
            ]}
          >
            {pagedRows.length === 0 && (
              <tr>
                <Td colSpan={9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No purchases match these filters.
                </Td>
              </tr>
            )}
            {pagedRows.map((g) => (
              <Tr key={g.id}>
                <Td mono style={{ fontWeight: 600 }}>
                  {g.id}
                </Td>
                <Td mono>{fmtDate(g.date)}</Td>
                <Td>{supById(g.supplier)?.name || g.supplierName}</Td>
                <Td mono style={{ color: "var(--fg-muted)" }}>
                  {g.supplierInv}
                </Td>
                <Td>
                  <Pill variant="neutral">{g.wh}</Pill>
                </Td>
                <Td mono right>
                  {g.items}
                </Td>
                <Td mono right>
                  {cbFmtNum(g.units)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtINR(g.value)}
                </Td>
                <Td>
                  <Pill variant={cbStatusVariant(g.status)} dot>
                    {g.status}
                  </Pill>
                </Td>
              </Tr>
            ))}
          </Table>

          <ToolbarBar>
            <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {startRow}–{endRow} of {cbFmtNum(totalRows)}
              </span>
              <Button size="sm" disabled={safePage <= 0} onClick={() => setPage(Math.max(0, safePage - 1))}>
                ← Prev
              </Button>
              <Button size="sm" disabled={safePage + 1 >= totalPages} onClick={() => setPage(safePage + 1)}>
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default PurchaseRegister;
